import {
    Engine,
    Scene,
    Label,
    Font,
    Color,
    Vector,
    vec,
    ScreenElement,
    TextAlign,
    CoordPlane
} from "excalibur";
import { Resources } from "../resources";

/**
 * GameOverScene - Death Screen
 * 
 * Displayed when the player's health reaches zero.
 * Shows "Macrophage Overwhelmed" message and a try again option.
 * 
 * Implements responsive layout that adapts to screen size.
 */
export class GameOverScene extends Scene {
    private titleLabel!: Label;
    private subtitleLabel!: Label;
    private tryAgainLabel!: Label;
    private background!: ScreenElement;
    private resizeHandler: (() => void) | null = null;
    private activationTime: number = 0;

    override onInitialize(engine: Engine): void {
        // Game over background image
        this.background = new ScreenElement({
            pos: vec(0, 0),
            width: 1,
            height: 1,
            anchor: Vector.Half,
        });
        const bgSprite = Resources.GameOverBackground.toSprite();
        this.background.graphics.use(bgSprite);
        this.add(this.background);

        // Game Over title
        this.titleLabel = new Label({
            text: 'GAME OVER',
            pos: vec(0, 0), // Will be set by updateLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 48,
                bold: true,
                color: Color.fromHex('#ef4444'),
                textAlign: TextAlign.Center,
            }),
            anchor: Vector.Half,
        });
        this.titleLabel.transform.coordPlane = CoordPlane.Screen;
        this.add(this.titleLabel);

        // Subtitle
        this.subtitleLabel = new Label({
            text: 'Macrophage Overwhelmed',
            pos: vec(0, 0), // Will be set by updateLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 20,
                color: Color.fromHex('#fca5a5'),
                textAlign: TextAlign.Center,
            }),
            anchor: Vector.Half,
        });
        this.subtitleLabel.transform.coordPlane = CoordPlane.Screen;
        this.add(this.subtitleLabel);

        // Try Again prompt
        this.tryAgainLabel = new Label({
            text: 'TAP TO TRY AGAIN',
            pos: vec(0, 0), // Will be set by updateLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 24,
                bold: true,
                color: Color.White,
                textAlign: TextAlign.Center,
            }),
            anchor: Vector.Half,
        });
        this.tryAgainLabel.transform.coordPlane = CoordPlane.Screen;
        this.add(this.tryAgainLabel);

        // Tap to restart
        engine.input.pointers.primary.once('down', () => {
            engine.goToScene('game');
        });

        // Initial layout
        this.updateLayout(engine);
    }

    override onActivate(): void {
        // Track when the scene became active
        this.activationTime = Date.now();

        // Stop game music
        Resources.GameMusic.stop();

        // Play game over sound
        Resources.GameOver.play();

        // Re-register tap handler each time scene activates (with 250ms delay)
        const handler = () => {
            const timeSinceActivation = Date.now() - this.activationTime;
            if (timeSinceActivation > 1000) {
                this.engine.goToScene('game');
            } else {
                // Re-register if tapped too early
                this.engine.input.pointers.primary.once('down', handler);
            }
        };
        this.engine.input.pointers.primary.once('down', handler);

        // Listen for browser resize events
        this.resizeHandler = () => this.updateLayout(this.engine);
        this.engine.screen.events.on('resize', this.resizeHandler);

        // Update layout immediately
        this.updateLayout(this.engine);
    }

    override onDeactivate(): void {
        // Cleanup listener
        if (this.resizeHandler) {
            this.engine.screen.events.off('resize', this.resizeHandler);
            this.resizeHandler = null;
        }
    }

    /**
     * Update element positions based on current screen size
     */
    private updateLayout(engine: Engine): void {
        const w = engine.drawWidth;
        const h = engine.drawHeight;

        // Scale background to cover full screen (cover mode: scale to fill, may crop)
        const bgSprite = Resources.GameOverBackground.toSprite();
        const scaleX = w / bgSprite.width;
        const scaleY = h / bgSprite.height;
        const scale = Math.max(scaleX, scaleY); // Cover mode - use larger scale
        bgSprite.scale = vec(scale, scale);
        this.background.graphics.use(bgSprite);
        // Center the background (anchor is Half, so position at screen center)
        this.background.pos = vec(w / 2, h / 2);

        // Title at 33% from top
        this.titleLabel.pos = vec(w / 2, h / 3);

        // Subtitle below title
        this.subtitleLabel.pos = vec(w / 2, h / 3 + 50);

        // Try Again at 66% from top
        this.tryAgainLabel.pos = vec(w / 2, h * 2 / 3);
    }

    override onPreUpdate(_engine: Engine, delta: number): void {
        // Pulsing animation for try again text
        const pulse = 0.7 + Math.sin(Date.now() / 300) * 0.3;
        this.tryAgainLabel.graphics.opacity = pulse;
    }
}
